import cx from 'classnames';
import { useAtom } from 'jotai';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useRef } from 'react';
import { FunctionComponent } from 'react';
import {
  FieldValues,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form';

import {
  brandAtom,
  modelAtom,
  versionAtom,
} from '../../../../../hooks/use-picker-selection';
import BrandSelect from '../../../../modules/v2/brand-picker/brand-selector/brand-select';
import ModelSelect from '../../../../modules/v2/brand-picker/brand-selector/model-select';
import VersionSelect from '../../../../modules/v2/brand-picker/brand-selector/version-select';
import { useAppContext } from '../../../../providers/app-context';

const CarSelect: FunctionComponent<{
  register: UseFormRegister<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  trigger: UseFormTrigger<FieldValues>;
  errors: Record<string, any>;
  vehicle?: any;
  isDrawer?: boolean;
  models?: any;
  versions?: any
}> = ({ register, getValues, setValue, trigger, errors, vehicle, isDrawer = false, models, versions }) => {
  const {
    features: { isBrandEnabled },
  } = useAppContext();
  const { t } = useTranslation();
  const [brand, setBrand] = useAtom(brandAtom);
  const [model, setModel] = useAtom(modelAtom);
  const [version, setVersion] = useAtom(versionAtom);

  useEffect(() => {
    if (vehicle) {
      if (vehicle.brand) {
        setBrand({
          ...vehicle.brand,
          value: vehicle.brand.slug,
          label: vehicle.brand.name,
        });
      }
      if (vehicle.model) {
        setModel({
          ...vehicle.model,
          value: vehicle.model.slug,
          label: vehicle.model.name,
        });
      }
      if (vehicle.version) {
        setVersion({
          ...vehicle.version,
          value: vehicle.version.slug,
          label: vehicle.version.name,
        });
      }
    }
  }, []);

  useEffect(() => {
    setValue('brand', brand?.value || brand?.slug || null);
    setValue('model', null);
    setValue('version', null);
  }, [brand, setValue, trigger]);

  useEffect(() => {
    setValue('model', model?.value || model?.slug || null);
    setValue('version', null);
    trigger('model');
  }, [model, setValue, trigger]);

  useEffect(() => {
    setValue('version', version?.value || version?.slug || null);
    if (versions?.versions?.length > 0) {
      trigger('version');
    }
  }, [version, setValue, trigger]);

  return (
    <div className={cx("grid  gap-6", isDrawer ? 'lg:grid-cols-1' : 'lg:grid-cols-3')}>
      {!isBrandEnabled && (
        <div>
          <BrandSelect
            register={
              register
                ? {
                  ...register('brand', {
                    validate: {
                      required: (v) =>
                        !getValues('reference') && !v
                          ? t(
                            'private-area:part-alerts-create.form.required-rule'
                          )
                          : undefined,
                    },
                  }),
                }
                : undefined
            }
            error={errors?.brand?.message}
            onChange={(e) => {
              setValue('brand', e.value);
              trigger(['part', 'reference', 'brand']);
            }}
            setValue={setValue}
          />
        </div>
      )}
      <div>
        <ModelSelect
          register={
            register
              ? {
                ...register('model', {
                  validate: {
                    required: (v) =>
                      getValues('brand') && !v
                        ? t('forms:errors.required')
                        : undefined,
                  },
                }),
              }
              : undefined
          }
          error={errors?.model?.message}
          setValue={setValue}
          models={models?.models || []}
          isModelsLoading={models?.isModelsLoading}
        />
      </div>
      <div>
        <VersionSelect
          register={
            register
              ? {
                ...register('version'),
              }
              : undefined
          }
          error={errors?.version?.message}
          setValue={setValue}
          versions={versions?.versions || []}
          isVersionsLoading={versions?.isVersionsLoading}
        />
      </div>
    </div>
  );
};

export default CarSelect;
